import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getRicevute, downloadRicevutaPdf, downloadRicevuteZip, exportRicevuteExcel, annullaRicevuta } from '../../services/api';
import { Table, Form, Spinner, Alert, Row, Col, Pagination, Card, Button, Badge, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../../common/MobileListLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilePdf, faFileExcel, faReceipt, faMoneyBillWave,
  faCalendarWeek, faCalendarDay, faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel,
  flexRender, createColumnHelper,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import SortableHeader from '../../common/SortableHeader';
import { buildServerFilters } from '../../common/serverFilterUtils';

// Salva sul PC la risposta blob usando il nome mandato dal server (filename* gestisce gli accenti)
function salvaBlobScaricato(res, nomeRiserva) {
  const cd = res.headers?.['content-disposition'] || '';
  let nome = nomeRiserva;
  const utf = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const semplice = cd.match(/filename="?([^";]+)"?/i);
  if (utf) nome = decodeURIComponent(utf[1]);
  else if (semplice) nome = semplice[1];
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nome);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// Con responseType blob anche gli errori arrivano come blob: ne estrae il "detail"
async function messaggioErroreBlob(err) {
  try {
    const testo = await err.response?.data?.text?.();
    return JSON.parse(testo).detail || err.message;
  } catch {
    return err.message;
  }
}

const columnHelper = createColumnHelper();

const STATO_FILTER_OPTIONS = [
  { value: 'valida', label: 'Valida' },
  { value: 'annullata', label: 'Annullata' },
];

const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const formatCurrency = (v) => `€ ${parseFloat(v || 0).toFixed(2)}`;

function getClienteDisplay(ricevuta) {
  const c = ricevuta.ordine?.cliente;
  if (!c) return '—';
  return c.denominazione_sociale || [c.nome, c.cognome].filter(Boolean).join(' ') || '—';
}

function RicevutaDetailModal({ ricevuta, onClose, onRefreshPdf, onConvertToFattura }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const cliente = getClienteDisplay(ricevuta);
  
  const handleSavePdf = async () => {
    setSaving(true);
    try {
      await onRefreshPdf(ricevuta.id);
    } finally {
      setSaving(false);
    }
  };
  
  const isAnnullata = ricevuta.stato === 'annullata';
  const isValida = !isAnnullata;
  
  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faReceipt} className="me-2 text-success" />
          Ricevuta {ricevuta.numero}
          {isAnnullata && <Badge bg="danger" className="ms-2">ANNULLATA</Badge>}
          {isValida && <Badge bg="success" className="ms-2">VALIDA</Badge>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col md={4}><small className="text-muted d-block">Nr. Ricevuta</small><strong>{ricevuta.numero}</strong></Col>
          <Col md={4}><small className="text-muted d-block">Data</small>{formatDate(ricevuta.data)}</Col>
          <Col md={4}><small className="text-muted d-block">Totale</small><span style={{fontSize:'1.2rem',color:'#198754',fontWeight:'bold'}}>{formatCurrency(ricevuta.totale)}</span></Col>
        </Row>
        {isAnnullata && (
          <Alert variant="warning" className="mb-3">
            <strong>Ricevuta annullata il {formatDate(ricevuta.data_annullamento)}</strong>
            {ricevuta.motivo_annullamento && <div className="mt-1"><small>Motivo: {ricevuta.motivo_annullamento}</small></div>}
          </Alert>
        )}
        <Row className="mb-3">
          <Col md={4}><small className="text-muted d-block">Nr. Ricevuta</small><strong>{ricevuta.numero}</strong></Col>
          <Col md={4}><small className="text-muted d-block">Data</small>{formatDate(ricevuta.data)}</Col>
          <Col md={4}><small className="text-muted d-block">Totale</small><span style={{fontSize:'1.2rem',color:'#198754',fontWeight:'bold'}}>{formatCurrency(ricevuta.totale)}</span></Col>
        </Row>
        <Row className="mb-3">
          <Col md={6}>
            <small className="text-muted d-block">Cliente</small>
            <strong>{cliente}</strong>
            {ricevuta.ordine?.cliente?.partita_iva && <div><small className="text-muted">P.IVA: {ricevuta.ordine.cliente.partita_iva}</small></div>}
            {ricevuta.ordine?.cliente?.codice_fiscale && <div><small className="text-muted">CF: {ricevuta.ordine.cliente.codice_fiscale}</small></div>}
          </Col>
          <Col md={6}>
            <small className="text-muted d-block">Ordine collegato</small>
            {ricevuta.ordine_id ? (
              <Button variant="link" size="sm" className="p-0"
                onClick={() => { onClose(); navigate(`/ordini/${ricevuta.ordine_id}`); }}>
                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />#{ricevuta.ordine_id}
              </Button>
            ) : '—'}
          </Col>
        </Row>
        {ricevuta.ordine?.dettagli && ricevuta.ordine.dettagli.length > 0 && (
          <>
            <h6 className="mt-3 mb-2">Righe della ricevuta</h6>
            <Table size="sm" bordered>
              <thead><tr><th>Descrizione</th><th className="text-end">Qtà</th><th className="text-end">Prezzo unit.</th><th className="text-end">Importo</th></tr></thead>
              <tbody>
                {ricevuta.ordine.dettagli.map((det, i) => {
                  let desc = det.descrizione_prodotto_libero || det.film?.titolo || (det.musica ? `${det.musica.artista} - ${det.musica.titolo}` : '') || det.servizio?.descrizione || '—';
                  let prezzo = det.prezzo_unitario_libero || det.prezzo_unitario || 0;
                  let totale = prezzo * det.quantita;
                  return (
                    <tr key={i}>
                      <td>{desc}</td>
                      <td className="text-end">{det.quantita}</td>
                      <td className="text-end">{formatCurrency(prezzo)}</td>
                      <td className="text-end">{formatCurrency(totale)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {isValida && (
            <Button variant="warning" onClick={() => onConvertToFattura(ricevuta)}>
              <FontAwesomeIcon icon={faReceipt} className="me-1" />
              Converti in Fattura
            </Button>
          )}
        </div>
        <div>
          <Button variant="secondary" onClick={onClose} className="me-2">Chiudi</Button>
          <Button variant="danger" onClick={handleSavePdf} disabled={saving}>
            {saving
              ? <><Spinner size="sm" animation="border" className="me-1" />Preparazione...</>
              : <><FontAwesomeIcon icon={faFilePdf} className="me-1" />Scarica PDF</>}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function RicevuteList() {
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const navigate = useNavigate();
  const [ricevute, setRicevute] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRicevuta, setSelectedRicevuta] = useState(null);
  const [savingPdf, setSavingPdf] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [totaleIncassato, setTotaleIncassato] = useState(0);
  const [statsPeriodo, setStatsPeriodo] = useState({});
  const [totalRicevute, setTotalRicevute] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-ricevute');
    return saved ? JSON.parse(saved) : [{ id: 'data', desc: true }];
  });
  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-ricevute');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    sessionStorage.setItem('list-sorting-ricevute', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-ricevute', JSON.stringify(columnFilters));
  }, [columnFilters]);

  const [showConvertModal, setShowConvertModal] = useState(false);
  const [ricevutaToConvert, setRicevutaToConvert] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [converting, setConverting] = useState(false);

  // Selezione multipla
  const [selectedIds, setSelectedIds] = useState([]);

  const buildFilters = useCallback(
    () => JSON.stringify(buildServerFilters(columnFilters)),
    [columnFilters]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sortParams = sorting.map(s => `${s.id}:${s.desc ? 'desc' : 'asc'}`);
      const res = await getRicevute((currentPage - 1) * itemsPerPage, itemsPerPage, sortParams, buildFilters());
      setRicevute(res.data.ricevute);
      setTotalRicevute(res.data.total);
      setTotaleIncassato(res.data.totale_incassato || 0);
      setStatsPeriodo(res.data.stats_periodo || {});
    } catch {
      setError('Errore nel caricamento delle ricevute.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, buildFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSavePdf = useCallback(async (ricevutaId) => {
    setSavingPdf(ricevutaId);
    try {
      const res = await downloadRicevutaPdf(ricevutaId);
      salvaBlobScaricato(res, `ricevuta_${ricevutaId}.pdf`);
    } catch (err) {
      toast.error(`Errore: ${await messaggioErroreBlob(err)}`);
    } finally {
      setSavingPdf(null);
    }
  }, []);

  const handleExportExcel = async () => {
    try {
      const res = await exportRicevuteExcel(buildFilters());
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ricevute_emesse_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Errore durante il download Excel');
    }
  };

  const handleConvertToFattura = (ricevuta) => {
    setRicevutaToConvert(ricevuta);
    setMotivo('Richiesta fattura a nome società');
    setShowConvertModal(true);
    setSelectedRicevuta(null);
  };

  const handleConfirmConvert = async () => {
    if (!motivo.trim()) {
      toast.error('Inserisci un motivo per l\'annullamento');
      return;
    }
    setConverting(true);
    try {
      await annullaRicevuta(ricevutaToConvert.id, motivo);
      toast.success(`Ricevuta ${ricevutaToConvert.numero} annullata con successo. Ordine #${ricevutaToConvert.ordine_id} ritorna in "Ordini da Fatturare".`);
      setShowConvertModal(false);
      fetchData();
      setTimeout(() => {
        navigate('/contabilita/ordini-da-fatturare');
      }, 1500);
    } catch (err) {
      toast.error(`Errore: ${err.response?.data?.detail || err.message}`);
    } finally {
      setConverting(false);
    }
  };

  // Gestione selezione multipla
  const toggleSelectAll = () => {
    if (selectedIds.length === ricevute.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(ricevute.map(r => r.id));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDownloadPdf = async () => {
    if (selectedIds.length === 0) return;
    
    // Una sola ricevuta: PDF diretto; più ricevute: un unico zip
    if (selectedIds.length === 1) {
      await handleSavePdf(selectedIds[0]);
      setSelectedIds([]);
      return;
    }
    setDownloadingZip(true);
    try {
      const res = await downloadRicevuteZip(selectedIds);
      salvaBlobScaricato(res, 'ricevute.zip');
      const errori = parseInt(res.headers?.['x-ricevute-errori'] || '0', 10);
      if (errori > 0) {
        toast.warning(`Zip scaricato, ma ${errori} ricevute non generate: vedi ERRORI.txt nello zip`);
      }
      setSelectedIds([]);
    } catch (err) {
      toast.error(`Errore: ${await messaggioErroreBlob(err)}`);
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleBulkExport = async () => {
    if (selectedIds.length === 0) return;
    try {
      const selectedFilters = { ids: selectedIds };
      const res = await exportRicevuteExcel(JSON.stringify(selectedFilters));
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ricevute_selezionate_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSelectedIds([]);
    } catch {
      toast.error('Errore durante l\'export delle ricevute selezionate');
    }
  };

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'select',
      header: () => (
        <Form.Check
          type="checkbox"
          checked={selectedIds.length === ricevute.length && ricevute.length > 0}
          onChange={toggleSelectAll}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => (
        <Form.Check
          type="checkbox"
          checked={selectedIds.includes(info.row.original.id)}
          onChange={() => toggleSelectRow(info.row.original.id)}
          onClick={e => e.stopPropagation()}
        />
      ),
    }),
    columnHelper.accessor('numero', {
      header: 'Nr. Ricevuta',
      meta: { filterVariant: 'text' },
      cell: info => <span className="fw-bold">{info.getValue()}</span>,
    }),
    columnHelper.accessor('data', {
      header: 'Data',
      meta: { filterVariant: 'date-range' },
      cell: info => formatDate(info.getValue()),
    }),
    columnHelper.accessor('stato', {
      header: 'Stato',
      meta: { filterVariant: 'select', filterOptions: STATO_FILTER_OPTIONS },
      cell: info => {
        const stato = info.getValue() || 'valida';
        return stato === 'annullata' ? (
          <Badge bg="danger">ANNULLATA</Badge>
        ) : (
          <Badge bg="success">VALIDA</Badge>
        );
      },
    }),
    columnHelper.display({
      id: 'cliente_denominazione',
      header: 'Cliente',
      meta: { filterVariant: 'text' },
      cell: info => getClienteDisplay(info.row.original),
    }),
    columnHelper.display({
      id: 'ordine',
      header: 'N. Ordine',
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => {
        const oid = info.row.original.ordine_id;
        return oid ? (
          <Button variant="link" size="sm" className="p-0"
            onClick={e => { e.stopPropagation(); navigate(`/ordini/${oid}`); }}>
            #{oid}
          </Button>
        ) : '—';
      },
    }),
    columnHelper.accessor('totale', {
      header: 'Totale',
      meta: { filterVariant: 'number-range' },
      cell: info => formatCurrency(info.getValue()),
    }),
    columnHelper.display({
      id: 'azioni',
      header: '',
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => (
        <Button size="sm" variant="outline-danger"
          onClick={e => { e.stopPropagation(); handleSavePdf(info.row.original.id); }}
          disabled={savingPdf === info.row.original.id}
          title="Scarica PDF">
          <FontAwesomeIcon icon={faFilePdf} spin={savingPdf === info.row.original.id} />
        </Button>
      ),
    }),
  ], [selectedIds, ricevute, savingPdf, navigate, handleSavePdf]);

  const table = useReactTable({
    data: ricevute,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualFiltering: true,
    manualSorting: true,
  });

  const hasActiveFilters = columnFilters.length > 0;

  const resetFilters = () => {
    table.resetColumnFilters();
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalRicevute / itemsPerPage);
  const renderPaginationItems = () => {
    if (totalPages <= 1) return null;
    const items = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    for (let i = start; i <= end; i++) {
      items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return items;
  };

  const renderCard = useCallback((ricevuta) => {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1"><FontAwesomeIcon icon={faReceipt} className="me-2 text-muted" />Ricevuta {ricevuta.numero}</h6>
            <small className="text-muted">{getClienteDisplay(ricevuta)}</small>
          </div>
          <Badge bg="success">{formatCurrency(ricevuta.totale)}</Badge>
        </div>
        <Row className="mb-2">
          <Col xs={6}>
            <small className="text-muted d-block">Data</small>
            {formatDate(ricevuta.data)}
          </Col>
          <Col xs={6}>
            {ricevuta.ordine_id && <Badge bg="info">Ordine #{ricevuta.ordine_id}</Badge>}
          </Col>
        </Row>
        <div className="mt-2 d-flex gap-1">
          <Button size="sm" variant="outline-primary" onClick={() => setSelectedRicevuta(ricevuta)}>Dettaglio</Button>
          <Button size="sm" variant="outline-danger" onClick={() => handleSavePdf(ricevuta.id)} disabled={savingPdf === ricevuta.id}>
            <FontAwesomeIcon icon={faFilePdf} spin={savingPdf === ricevuta.id} />
          </Button>
        </div>
      </div>
    );
  }, [savingPdf, handleSavePdf]);

  const ricevuteOggi = ricevute.filter(r => r.data === new Date().toISOString().slice(0, 10)).length;

  return (
    <>
      {selectedRicevuta && (
        <RicevutaDetailModal
          ricevuta={selectedRicevuta}
          onClose={() => setSelectedRicevuta(null)}
          onRefreshPdf={handleSavePdf}
          onConvertToFattura={handleConvertToFattura}
        />
      )}
      <Modal show={showConvertModal} onHide={() => setShowConvertModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Conversione in Fattura</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Stai per annullare la ricevuta <strong>{ricevutaToConvert?.numero}</strong> per convertirla in fattura.</p>
          <p>L'ordine <strong>#{ricevutaToConvert?.ordine_id}</strong> tornerà nella sezione "Ordini da Fatturare" e potrai emettere fattura a nome di un soggetto diverso (con P.IVA).</p>
          <Form.Group className="mt-3">
            <Form.Label>Motivo annullamento ricevuta</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Es: Richiesta fattura a nome società"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConvertModal(false)} disabled={converting}>
            Annulla
          </Button>
          <Button variant="warning" onClick={handleConfirmConvert} disabled={converting || !motivo.trim()}>
            {converting ? (
              <><Spinner size="sm" animation="border" className="me-1" />Annullamento...</>
            ) : (
              'Conferma Annullamento'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <Row className="mb-3 align-items-center">
        <Col><h1 className="h3 mb-0">Ricevute Emesse</h1></Col>
        <Col xs="auto" className="d-flex gap-2">
          {hasActiveFilters && (
            <Button variant="outline-secondary" onClick={resetFilters}>
              Azzera filtri <Badge bg="danger" className="ms-1">{columnFilters.length}</Badge>
            </Button>
          )}
          <Button variant="outline-success" onClick={handleExportExcel}>
            <FontAwesomeIcon icon={faFileExcel} className="me-1" />Excel
          </Button>
        </Col>
      </Row>
      {/* KPI */}
      <Row className="mb-3">
        <Col md={3}>
          <Card className="border-success">
            <Card.Body className="py-2">
              <small className="text-muted d-block">Totale incassato</small>
              <span className="fs-5 fw-bold text-success"><FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />{formatCurrency(totaleIncassato)}</span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-primary">
            <Card.Body className="py-2">
              <small className="text-muted d-block">Ricevute oggi</small>
              <span className="fs-5 fw-bold text-primary"><FontAwesomeIcon icon={faCalendarDay} className="me-1" />{ricevuteOggi}</span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-info">
            <Card.Body className="py-2">
              <small className="text-muted d-block">Ultima settimana</small>
              <span className="fs-5 fw-bold text-info"><FontAwesomeIcon icon={faCalendarWeek} className="me-1" />{statsPeriodo.ultima_settimana || 0}</span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body className="py-2">
              <small className="text-muted d-block">Ultimo mese</small>
              <span className="fs-5 fw-bold text-secondary"><FontAwesomeIcon icon={faReceipt} className="me-1" />{statsPeriodo.ultimo_mese || 0}</span>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Barra azioni bulk */}
      {selectedIds.length > 0 && !isMobile && (
        <Alert variant="info" className="mb-3 d-flex justify-content-between align-items-center">
          <span>
            <strong>{selectedIds.length}</strong> ricevuta/e selezionata/e
          </span>
          <div>
            <Button size="sm" variant="danger" onClick={handleBulkDownloadPdf} className="me-2" disabled={downloadingZip}>
              <FontAwesomeIcon icon={faFilePdf} className="me-1" spin={downloadingZip} />
              {downloadingZip ? 'Preparazione...' : `Scarica PDF (${selectedIds.length})`}
            </Button>
            <Button size="sm" variant="success" onClick={handleBulkExport} className="me-2">
              <FontAwesomeIcon icon={faFileExcel} className="me-1" />
              Esporta selezionate
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setSelectedIds([])}>
              Deseleziona tutto
            </Button>
          </div>
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0 p-md-3">
          {loading && <div className="text-center py-4"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {!loading && !error && (
            isMobile ? (
              <MobileListLayout
                items={ricevute}
                renderCard={renderCard}
                entityName="ricevuta"
                showCreateButton={false}
                showSelectAll={false}
              />
            ) : (
              <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    {table.getHeaderGroups().map(hg => (
                      <tr key={hg.id}>
                        {hg.headers.map(header => (
                          <SortableHeader key={header.id} header={header} table={table} stickyColumns={[]} />
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                      <tr><td colSpan={8} className="text-center text-muted py-4">Nessuna ricevuta trovata</td></tr>
                    ) : (
                      table.getRowModel().rows.map(row => (
                        <tr key={row.id} style={{ cursor: 'pointer' }} onDoubleClick={() => setSelectedRicevuta(row.original)}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )
          )}
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Form.Group as={Row} className="align-items-center mb-0">
            <Col xs="auto"><Form.Label className="mb-0 small">Righe</Form.Label></Col>
            <Col xs="auto">
              <Form.Select size="sm" value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </Col>
            <Col xs="auto"><small className="text-muted">Totale: {totalRicevute}</small></Col>
          </Form.Group>
          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
            {renderPaginationItems()}
            <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages} />
            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages} />
          </Pagination>
        </Card.Footer>
      </Card>
    </>
  );
}

export default RicevuteList;
